import axios from "axios";
import { STATUS_CODE_CANCEL } from "constant/app";
import get from "lodash/get";
import { CANCEL } from "redux-saga";
import { auth } from "store/auth";
import * as QS from "util/query-string";

const HEADER_AUTHORIZATION_TOKEN = "Authorization";
const HEADER_CONTENT_TYPE = "Content-Type";


class BaseService {
  get(url, params, options = {}) {
    const { dataType = "json", cancelSource } = options;

    return _makeCancelableRequest(
      {
        url,
        params,
        headers: _getHeaders(dataType),
        method: "GET",
      },
      cancelSource,
    );
  }

  post(url, params, dataType = "json", headers = null) {
    const data = dataType === "json" ? JSON.stringify(params) : params;

    return _makeCancelableRequest({
      data,
      url,
      headers: headers || _getHeaders(dataType),
      method: "POST",
    });
  }

  /**
   * Makes a PUT request, supporting S3 uploads with presigned URLs.
   * @param {string} url - The target URL (S3 presigned URL or API endpoint).
   * @param {any} params - The data to send (e.g., File for S3, JSON for API).
   * @param {string} dataType - Data type ('json' or 'binary', default 'json').
   * @param {Object} additionalHeaders - Optional headers (e.g., x-amz-meta-*, x-amz-acl).
   * @param {boolean} isS3Upload - True if this is an S3 upload (default false).
   * @returns {Promise} - Resolves to the response or throws an error.
   */
  async put(url, params, dataType = "json", additionalHeaders = {}, isS3Upload = false) {
    // Validate S3 presigned URL if isS3Upload is true
    if (isS3Upload && !_isValidS3PresignedUrl(url)) {
      throw new Error("Invalid S3 presigned URL");
    }

    const data = dataType === "json" ? JSON.stringify(params) : params;

    try {
      const headers = _getHeaders(dataType, params, isS3Upload, additionalHeaders);
      const response = await _makeCancelableRequest({
        data,
        url,
        headers,
        method: "PUT",
      });

      // Handle S3-specific response
      if (isS3Upload && response.status === 200) {
        return {
          success: true,
          message: "File uploaded successfully to S3",
          data: response.data,
        };
      }
      return response; // Standard response for non-S3 requests
    } catch (error) {
      // Handle S3-specific errors
      if (isS3Upload) {
        if (axios.isCancel(error)) {
          throw new Error("Upload canceled");
        } else if (error.response) {
          switch (error.response.status) {
            case 403:
              throw new Error("Permission denied: Check S3 presigned URL or bucket permissions");
            case 400:
              throw new Error("Bad request: Invalid presigned URL or mismatched headers");
            case 404:
              throw new Error("S3 bucket or resource not found");
            default:
              throw new Error(`S3 upload failed: ${error.message}`);
          }
        } else {
          console.error("S3 network error:", { message: error.message, config: error.config });
          throw new Error(`Network error: ${error.message}`);
        }
      }
      throw error; // Rethrow for non-S3 requests
    }
  }

  delete(url, params, dataType = "json") {
    const data = JSON.stringify(params);

    return _makeCancelableRequest({
      data,
      url,
      headers: _getHeaders(dataType),
      method: "DELETE",
    });
  }
}

// Validates S3 presigned URL
function _isValidS3PresignedUrl(url) {
  const parsedUrl = new URL(url);
  try {
    const parsedUrl = new URL(url);
    return (
      parsedUrl.hostname.endsWith("s3.amazonaws.com") ||
      parsedUrl.hostname.includes("s3-") ||
      parsedUrl.searchParams.has("X-Amz-Signature")
    );
  } catch (e) {
    return false;
  }
}

function _makeCancelableRequest(config, cancelSource) {
  const source = cancelSource ? cancelSource : axios.CancelToken.source();
  const request = axios({
    ...config,
    cancelToken: source.token,
    paramsSerializer: QS.stringify,
  });
  request[CANCEL] = () => {
    console.info("[saga] xhr cancellation request:", config.url, config.method);
    source.cancel();
  };

  return request;
}

// Generates headers for requests
function _getHeaders(dataType = "json", params = null, isS3Upload = false, additionalHeaders = {}) {
  const headers = {};

  if (!isS3Upload) {
    const token = auth.getToken();
    if (token) {
      headers[HEADER_AUTHORIZATION_TOKEN] = `Bearer ${token}`;
    }
  }

  if (dataType === "json") {
    headers[HEADER_CONTENT_TYPE] = "application/json";
  } else if (dataType === "form") {
    // Let axios set Content-Type: multipart/form-data with boundary
  } else if (isS3Upload && params instanceof File && !additionalHeaders[HEADER_CONTENT_TYPE]) {
    headers[HEADER_CONTENT_TYPE] = params.type || "application/octet-stream";
  } else {
    headers[HEADER_CONTENT_TYPE] = "application/octet-stream";
  }

  const mergedHeaders = { ...headers, ...additionalHeaders };

  Object.keys(additionalHeaders).forEach(key => {
    if (!key.toLowerCase().startsWith("x-amz-") && key !== HEADER_CONTENT_TYPE) {
      console.warn(`Unexpected header in additionalHeaders: ${key}`);
    }
  });

  return mergedHeaders;
}

function _handleError(apiError) {
  if (apiError instanceof axios.Cancel) {
    apiError.status = STATUS_CODE_CANCEL;

    return Promise.reject(apiError);
  }

  return Promise.reject(get(apiError, "response", {}));
}

function _handleSuccess(response) {
  return response;
}

axios.interceptors.response.use(_handleSuccess, _handleError);

const baseService = new BaseService();

export default baseService;
export {
  _getHeaders,
  _handleError,
  _handleSuccess,
  _makeCancelableRequest,
  BaseService,
  baseService,
  HEADER_AUTHORIZATION_TOKEN,
  HEADER_CONTENT_TYPE,
};